import { useCallback, useEffect, useRef } from 'react';

import connect from 'components/lib/connect';
import CustomerViewerActivity from 'models/customer_viewer_activity';
import { targetForCompositionType } from 'models/customer_viewer_activity_target';
import ReportCustomerViewing from 'actions/customer_viewers/report_customer_viewing';
import StopCustomerViewing from 'actions/customer_viewers/stop_customer_viewing';
import useExecuteAction from 'components/hooks/use_execute_action';
import { VIEWER_TTL_SECONDS } from 'scripts/adapters/gateways/customer_viewers_gateway';

// A third of the record's expiry on the broker, so two dropped publishes in a row
// still don't make a present agent flicker off other screens.
export const HEARTBEAT_INTERVAL_MS = 15 * 1000;

/**
 * CustomerViewingReporter is a renderless component that publishes that this
 * session is on a customer page, what it's doing there, and keeps saying so while
 * it is.
 *
 * Presence has to be re-proven rather than announced once: a closed laptop, a
 * crashed tab, or a dropped network sends no goodbye, so the absence of a
 * heartbeat is the only signal anyone can trust. The clean-exit clear on unmount is
 * an optimization on top of that, not the mechanism.
 *
 * Reporting pauses while the tab is hidden, because an agent with the customer
 * parked in a background tab is not looking at it.
 */
export function CustomerViewingReporter({ activity, activityTarget, currentAgentId, customerId, isEnabled }) {
  const executeAction = useExecuteAction();

  const arrivedAtRef = useRef(null);
  const lastReportedAtRef = useRef(null);

  // The heartbeat interval closes over `report` once, so the activity it should
  // send is read from a ref rather than captured.
  const activityRef = useRef();
  activityRef.current = { activity, activityTarget };

  // What we last actually sent, so an activity change can tell whether it still
  // needs reporting or was already covered by the arrival report.
  const lastSentRef = useRef(null);

  const report = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    // Arrival is stamped here rather than on mount, and so after the hidden guard: a
    // tab that mounts in the background is not present yet. A gap of a whole expiry
    // means the record was already dropped and everyone watched this agent leave, so
    // coming back is a new arrival rather than a continuation. `>=` because the
    // expiry is the first instant the record is gone, not the last it is held.
    const now = Date.now();
    if (!arrivedAtRef.current || now - lastReportedAtRef.current >= VIEWER_TTL_SECONDS * 1000) {
      arrivedAtRef.current = new Date(now).toISOString();
    }
    lastReportedAtRef.current = now;

    const current = activityRef.current;
    lastSentRef.current = activityKey(current);
    executeAction(ReportCustomerViewing, {
      ...current,
      arrivedAt: arrivedAtRef.current,
      customerId,
    });
  }, [customerId, executeAction]);

  // Keyed on the session rather than the activity: re-running this when the activity
  // changes would reset the arrival time and fire a spurious departure on the way
  // out.
  useEffect(() => {
    if (!isEnabled || !customerId || !currentAgentId) {
      return undefined;
    }

    arrivedAtRef.current = null;
    lastReportedAtRef.current = null;
    lastSentRef.current = null;

    report();
    const intervalId = setInterval(report, HEARTBEAT_INTERVAL_MS);

    // Report straight away on becoming visible instead of waiting out the rest of
    // the interval, so returning to a tab doesn't leave the agent invisible for
    // most of a TTL.
    document.addEventListener('visibilitychange', report);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', report);

      // A tab that stayed hidden its whole life never published, and clearing a
      // record that was never written retains a tombstone for it.
      const didReport = lastSentRef.current !== null;

      arrivedAtRef.current = null;
      lastReportedAtRef.current = null;
      lastSentRef.current = null;

      if (didReport) {
        executeAction(StopCustomerViewing, { customerId });
      }
    };
  }, [currentAgentId, customerId, executeAction, isEnabled, report]);

  // Push an activity change out immediately rather than letting a colleague see a
  // stale label for up to a heartbeat. Skipped when the arrival report already
  // carried this activity, which is the case on mount.
  useEffect(() => {
    if (!isEnabled || !customerId || !currentAgentId) {
      return;
    }
    if (lastSentRef.current === activityKey({ activity, activityTarget })) {
      return;
    }

    report();
  }, [activity, activityTarget, currentAgentId, customerId, isEnabled, report]);

  return null;
}

function activityKey({ activity, activityTarget }) {
  return `${activity || ''}|${activityTarget || ''}`;
}

/**
 * Activity is derived from store state rather than reported by the composition
 * actions, because a composition is opened, switched between, closed, submitted,
 * discarded and restored from local storage on reload — hooking each of those means
 * every new path is a chance to leave a colleague looking at a label that stopped
 * being true.
 *
 * Nothing is reported until `currentAgentId` is set. This effect runs on mount,
 * which on a page load landing straight on a customer can precede LogIn — and until
 * LogIn calls initGateways the gateway's endpoint has no orgId, so the publish would
 * throw while building its url. LogIn sets the current agent *after* initGateways,
 * which makes it a safe readiness signal.
 */
export function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const currentAgent = getProvider('currentAgent').get();
  const currentLocation = getProvider('currentLocation').get();
  const compositions = getProvider('compositions');
  const compositionId = currentLocation && currentLocation.currentCompositionId;
  const composition = compositionId ? compositions.findBy({ id: compositionId }) : null;

  return {
    activity: composition ? CustomerViewerActivity.COMPOSING : CustomerViewerActivity.VIEWING,
    // Left unset for VIEWING: a target on an activity that acts on nothing is
    // meaningless to every reader.
    activityTarget: composition ? targetForCompositionType(composition.contentType()) : undefined,
    currentAgentId: currentAgent && currentAgent.id,
    isEnabled: isFeatureEnabled('agentsViewingCustomer'),
  };
}

export default connect(mapStateToProps)(CustomerViewingReporter);
